import * as mat from './lib/jsmatrix';
import { MatF32, VecF32 } from './lib/jsmatrix';

import {
    distanceSquaredPointLineSegment,
    transformPoints,
    pointInBounds2d,
    computeBounds,
    intersectLinePlane,
    pointInside,
    mat2points,
    insidePoly
} from './pointUtils.js'

import {
    Data, genID, copy
} from './utils.js';

import {
    MIN_GRID_SIZE
} from './commonView.js';


function createProperties({
    z = 0,
    showBoundVanishingLines = false,
    showBoundGridBottom = true,
    showBoundGridTop = true,
    showSideGridFront = true,
    showSideGridBack = false,
    gridCellLength = MIN_GRID_SIZE,
    gridCellHeight = MIN_GRID_SIZE,
    displayCellLength = true,
    sideGridStart = 0
}) {

    return {
        z,
        showBoundVanishingLines,
        showBoundGridBottom,
        showBoundGridTop,
        showSideGridFront,
        showSideGridBack,
        gridCellLength,
        displayCellLength,
        gridCellHeight,
        sideGridStart
    };
}


class Transform {
    constructor(t = VecF32.from([0, 0, 0]),
        alpha = 0,
        scale = VecF32.from([1, 1, 1])) {
        this.t = t;
        this.alpha = alpha;
        this.scale = scale;
        this.update();

    }

    static new(t, alpha, scale) {
        return new Transform(t, alpha, scale);
    }
    toMat() {
        return this.m;
    }

    toJSON() {
        return {
            t: mat.toJSON(this.t),
            alpha: this.alpha,
            scale: mat.toJSON(this.scale)
        };
    }

    update() {
        this.m = mat.mult(
            mat.translation(this.t),
            mat.mult(
                mat.axisAngle4(mat.Z, this.alpha),
                mat.scaling(this.scale)
            )
        );
    }
    setAlpha(alpha) {
        this.alpha = alpha;
        this.update();
    }
    setTranslation(t) {
        this.t = t;
        this.update();
    }

    setScale(s) {
        this.scale = s;
        this.update();
    }

    copy() {
        return Transform.new(this.t, this.alpha, this.scale);
    }
}

class DrawObject {

    /**
     * 
     * @param {*} drawable 
     * @param {Data} styleData 
     * @param {*} M 
     * @param {DisplayOptions} options 
     */
    constructor(drawable, styleData, transform, options, id) {
        this.drawable = drawable;
        this.styleData = styleData !== undefined ? styleData : Data.new();

        this.setTransform(mat.optional(transform, Transform.new()));

        this.options = createProperties(options !== undefined ? options : {});
        this.id = mat.optional(id, genID());
    }

    static new(drawable, styleData, transform, options, id) {
        return new DrawObject(drawable, styleData, transform, options, id);
    }

    toJSON() {
        return {
            drawobject: {
                drawable: this.drawable,
                transform: this.transform,
                properties: this.options,
                styleData: this.styleData,
                id: this.id
            }
        };
    }

    draw(output, VP, clipplanes) {
        VP = VP !== undefined ? VP : MatF32.id(4, 4);
        this.drawableWorld.draw(output, this.styleData, VP, clipplanes);
    }

    setTransform(transform) {
        this.transform = transform;
        const draw = this.drawable;
        const center = draw.center;

        this.M = mat.mult(mat.translation(center),
            mat.mult(this.transform.toMat(), mat.translation(mat.neg(center))));


        this.drawableWorld = this.drawable.transform(this.M);
        this.xyboundsWorld = transformPoints(this.drawable.xybounds, this.M);

    }
    clone() {
        return DrawObject.new(this.drawable.clone(),
            this.styleData.copy(), this.transform.copy(), copy(this.options));
    }

}

class Displayable {
    constructor(drawable, styleData, z, id) {
        this.drawable = drawable;
        this.styleData = styleData;
        this.z = z;
        this.id = mat.optional(id, genID());
    }
    static new(drawable, styleData, z, id) {
        return new Displayable(drawable, styleData, z, id);
    }
}



class Polygon {

    constructor(points) {

        this.points = points;
        const [bmin, bmax] = computeBounds(points);
        this.bmin = bmin;
        this.bmax = bmax;
        this.center = mat.scale(mat.add(bmin, bmax), 0.5);


        const xybounds = MatF32.uninitialized(3, 4);
        mat.insert(mat.col(xybounds, 0), bmin);
        mat.insert(mat.col(xybounds, 1), VecF32.from([bmax.at(0), bmin.at(1), bmin.at(2)]));
        mat.insert(mat.col(xybounds, 2), bmax);
        mat.insert(mat.col(xybounds, 3), VecF32.from([bmin.at(0), bmax.at(1), bmin.at(2)]));

        this.xybounds = xybounds;

    }

    static new(points) {
        return new Polygon(points);
    }

    toJSON() {
        return {
            type: "Polygon",
            points: mat.toJSON(this.points)
        }
    }

    transform(M) {
        let p = this.points;

        p = mat.PaddedView.new(p, 4, p.cols(), 1, 1);
        p = mat.mult(M, p);
        p = mat.homogenize(p);

        return Polygon.new(MatF32.copy(mat.block(p, 0, 0, 3, p.cols())));
    }

    draw(output, styleData) {

        let p = this.points;

        let drawpoints = mat2points(p);


        output.drawPoly(drawpoints, styleData);
    }


    clip(plane) {
        const outputList = [];
        const inputPoints = this.points;
        const numIn = inputPoints.cols();

        let A = mat.col(inputPoints, numIn - 1);

        const pushPoint = (p, list) => list.push(...mat.toArray(p));
        let num = 0;
        for (let i = 0; i < numIn; i++) {
            const B = mat.col(inputPoints, i);


            if (pointInside(B, plane)) {
                if (pointInside(A, plane)) {
                    pushPoint(B, outputList);
                    num++;
                }
                else {
                    const [S,] = intersectLinePlane(A, B, plane);
                    pushPoint(S, outputList);
                    num++;
                    pushPoint(B, outputList);
                    num++;
                }
            }
            else {
                if (pointInside(A, plane)) {
                    const [S,] = intersectLinePlane(A, B, plane);
                    pushPoint(S, outputList);
                    num++;
                }
            }

            A = B;
        }

        if (num === 0) {
            return null;
        }

        return Polygon.new(MatF32.from(outputList, 3, num));

    }

    clone() {
        return Polygon.new(mat.copy(this.points));
    }

    isInsideXY(p, tolerance) {
        const points = this.points;

        if (!pointInBounds2d(p, this.bmin, this.bmax)) {
            return false;
        }

        const inside = insidePoly(p, points);
        if (inside) {
            return true;
        }

        // if point is ouside, check if distance lies withing tolerance
        const num = points.cols();
        const t2 = tolerance * tolerance;
        const ci = i => MatF32.copy(mat.subvec(mat.col(points, i), 0, 2));

        for (let i = 0; i < num; i++) {
            const a = ci(i);
            const b = ci((i + 1) % num);

            const d2 = distanceSquaredPointLineSegment(p, a, b);
            if (d2 < t2) {
                return true;
            }
        }

        return false;
    }

}

class LineStrip {
    constructor(points) {
        this.points = points;
        const [bmin, bmax] = computeBounds(points);
        this.bmin = bmin;
        this.bmax = bmax;
        this.center = mat.scale(mat.add(bmin, bmax), 0.5);


        const xybounds = MatF32.uninitialized(3, 4);
        mat.insert(mat.col(xybounds, 0), bmin);
        mat.insert(mat.col(xybounds, 1), VecF32.from([bmax.at(0), bmin.at(1), bmin.at(2)]));
        mat.insert(mat.col(xybounds, 2), bmax);
        mat.insert(mat.col(xybounds, 3), VecF32.from([bmin.at(0), bmax.at(1), bmin.at(2)]));

        this.xybounds = xybounds;

    }

    static new(points) {
        return new LineStrip(points);
    }
    toJSON() {
        return {
            type: "LineStrip",
            points: mat.toJSON(this.points)
        }
    }

    transform(M) {
        let p = this.points;

        p = mat.PaddedView.new(p, 4, p.cols(), 1, 1);
        p = mat.mult(M, p);
        p = mat.homogenize(p);

        return LineStrip.new(MatF32.copy(mat.block(p, 0, 0, 3, p.cols())));
    }

    draw(output, styleData) {

        let p = this.points;
        let drawpoints = mat2points(p);


        output.drawLineStrip(drawpoints, styleData);
    }


    clip(plane) {
        const outputList = [];

        const inputPoints = this.points;
        const numIn = inputPoints.cols();


        let num = 0;
        const pushPoint = (p) => {
            outputList.push(...mat.toArray(p));
            num++;
        };
        for (let i = 1; i < numIn; i++) {

            // TODO
            const A = mat.col(inputPoints, i - 1);
            const B = mat.col(inputPoints, i);


            if (pointInside(B, plane)) {
                if (pointInside(A, plane)) {
                    if (i === 1) {
                        pushPoint(A);
                    }
                    pushPoint(B);
                }
                else {
                    const [S,] = intersectLinePlane(A, B, plane);
                    pushPoint(S);
                    pushPoint(B);
                }
            }
            else {
                if (pointInside(A, plane)) {
                    if (i === 1) {
                        pushPoint(A);
                    }
                    const [S,] = intersectLinePlane(A, B, plane);
                    pushPoint(S);
                }
            }
        }

        if (num === 0) {
            return null;
        }

        return LineStrip.new(MatF32.from(outputList, 3, num));

    }

    clone() {
        return LineStrip.new(mat.copy(this.points));
    }

    isInsideXY(p, tolerance) {

        const vtol = VecF32.from([tolerance, tolerance, tolerance]);
        if (!pointInBounds2d(p, mat.sub(this.bmin, vtol), mat.add(this.bmax, vtol))) {
            return false;
        }
        const points = this.points;
        const num = points.cols();

        const t2 = tolerance * tolerance;

        for (let i = 0; i < num - 1; i++) {
            const a = VecF32.copy(mat.subvec(mat.col(points, i), 0, 2));
            const b = VecF32.copy(mat.subvec(mat.col(points, i + 1), 0, 2));
            if (distanceSquaredPointLineSegment(p, a, b) < t2) {
                return true;
            }
        }

        return false;
    }

}

class Point {
    constructor(point, radius) {
        this.points = point;
        this.bmin = point;
        this.bmax = point;
        this.center = point;
        this.radius = mat.optional(radius, 2);

        const xybounds = MatF32.uninitialized(3, 4);
        mat.insert(mat.col(xybounds, 0), point);
        mat.insert(mat.col(xybounds, 1), point);
        mat.insert(mat.col(xybounds, 2), point);
        mat.insert(mat.col(xybounds, 3), point);

        this.xybounds = xybounds;
    }

    static new(point, radius) {
        return new Point(point, radius);
    }

    toJSON() {
        return {
            type: "Point",
            points: mat.toJSON(this.points),
            radius: this.radius
        }
    }
    transform(M) {
        let p = this.points;

        p = mat.PaddedView.new(p, 4, p.cols(), 1, 1);
        p = mat.mult(M, p);
        p = mat.homogenize(p);

        return Point.new(MatF32.copy(mat.block(p, 0, 0, 3, p.cols())), this.radius);
    }

    draw(output, styleData) {

        let p = this.points;
        output.drawArc(mat.toArray(p), this.radius, 0, Math.PI * 2, styleData);
    }


    clip(plane) {
        const p = this.points;
        if (pointInside(p, plane)) {
            return Point.new(p, this.radius);
        }

        return null;

    }
    clone() {
        return Point.new(mat.copy(this.points), this.radius);
    }

    isInsideXY(p, tolerance) {
        return mat.norm2Squared(mat.sub(p, this.points)) < tolerance * tolerance;

    }

}

class Text {
    constructor(point, text) {
        this.points = point;
        this.bmin = point;
        this.bmax = point;
        this.center = point;
        this.text = text;

        const xybounds = MatF32.uninitialized(3, 4);
        mat.insert(mat.col(xybounds, 0), point);
        mat.insert(mat.col(xybounds, 1), point);
        mat.insert(mat.col(xybounds, 2), point);
        mat.insert(mat.col(xybounds, 3), point);

        this.xybounds = xybounds;
    }

    static new(point, text) {
        return new Text(point, text);
    }

    toJSON() {
        return {
            type: "Text",
            points: mat.toJSON(this.points),
            text: this.text
        }
    }
    transform(M) {
        let p = this.points;

        p = mat.PaddedView.new(p, 4, p.cols(), 1, 1);
        p = mat.mult(M, p);
        p = mat.homogenize(p);

        return Text.new(MatF32.copy(mat.block(p, 0, 0, 3, p.cols())), this.text);
    }

    draw(output, styleData) {

        let p = this.points;
        output.drawText(mat.toArray(p), this.text, styleData);
    }


    clip(plane) {
        const p = this.points;
        if (pointInside(p, plane)) {
            return Text.new(p, this.text);
        }

        return null;

    }
    clone() {
        return Text.new(mat.copy(this.points), this.text);
    }

    isInsideXY(p, tolerance) {
        return true;
    }

}


export {
    Transform,
    DrawObject,
    Displayable,
    Polygon,
    LineStrip,
    Point,
    Text,
    createProperties

};